import type {
  ReportSchedule,
  ScheduleSetting,
} from '@components/SitemapReport/schedule'
import type { ScheduleDto } from '@octopus-do/octopus-api-client'
import { parseNestError } from '@store/reducers/dashboard/utils'
import { useCallback, useEffect, useRef, useState } from 'react'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { NestClient } from '@/nest'

const ScheduleEvents: Record<ReportSchedule, GtmTriggerName> = {
  daily: GtmTriggerName.REPORT_SCHEDULE_SET_DAY,
  monthly: GtmTriggerName.REPORT_SCHEDULE_SET_MONTH,
  off: GtmTriggerName.REPORT_SCHEDULE_SET_DISABLE,
  weekly: GtmTriggerName.REPORT_SCHEDULE_SET_WEEK,
}

// A frequency picked for the first time starts on the day the design shows:
// Monday, or the 1st.
const DEFAULT_DAYS: Record<ReportSchedule, number[]> = {
  daily: [],
  monthly: [1],
  off: [],
  weekly: [1],
}

const DEFAULT_HOUR = 9

const browserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

const isSchedule = (value?: string): value is ReportSchedule =>
  Boolean(value && value in DEFAULT_DAYS)

const sameSetting = (left: ScheduleSetting, right: ScheduleSetting) =>
  left.schedule === right.schedule &&
  left.hour === right.hour &&
  left.days.join(',') === right.days.join(',')

// An API that does not know about schedules yet answers without one, and a
// dialog that took that answer literally would show four options with none of
// them picked and a Save button that never lit up. A setting it cannot read is
// the same as no schedule.
const toSetting = (setting?: null | Partial<ScheduleDto>): ScheduleSetting => {
  const answered = setting?.schedule
  const schedule = isSchedule(answered) ? answered : 'off'

  return {
    days: setting?.days ?? [],
    hour: setting?.hour ?? DEFAULT_HOUR,
    schedule,
    timezone: browserTimezone(),
  }
}

// The screen behind the dialog already read the schedule with the rest of the
// overview, so the dialog opens on that setting and never on a spinner. It
// still asks again in the background — a schedule someone changed in another
// tab would otherwise open here already stale — and only replaces what is on
// screen while nothing has been touched.
export const useScheduleCrawl = (
  uuid?: string,
  onSaved?: () => void,
  initial?: ScheduleDto | null
) => {
  const seed = initial ? toSetting(initial) : null
  const [saved, setSaved] = useState<ScheduleSetting | null>(seed)
  const [draft, setDraft] = useState<ScheduleSetting | null>(seed)
  const [loading, setLoading] = useState(!seed)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<null | string>(null)

  // A background answer must not pull the controls out from under someone who
  // is already picking days, so it stands down as soon as the draft is touched.
  const touched = useRef(false)
  const seeded = useRef(Boolean(seed))

  useEffect(() => {
    if (!uuid) {
      return
    }

    NestClient.sitemapReport
      .overview(uuid)
      .then((data) => {
        if (touched.current) {
          return
        }

        const setting = toSetting(data.schedule)

        setSaved(setting)
        setDraft(setting)
      })
      .catch((cause) => {
        // With a setting already on screen a failed refresh is not worth an
        // error in place of it — the save is what has to succeed.
        if (!touched.current && !seeded.current) {
          setError(parseNestError(cause))
        }
      })
      .finally(() => setLoading(false))
  }, [uuid])

  // Each frequency keeps its own days, and a frequency picked for the first
  // time starts on the day the design shows: Monday, or the 1st.
  const setSchedule = useCallback((schedule: ReportSchedule) => {
    touched.current = true

    setDraft((current) => {
      if (!current) {
        return current
      }

      // Weekday numbers and days of the month are the same numbers and mean
      // different things, so days only carry over within the same frequency.
      const kept = current.schedule === schedule ? current.days : []
      const days = DEFAULT_DAYS[schedule]

      if (kept.length) {
        return { ...current, days: kept, schedule }
      }

      return { ...current, days, schedule }
    })
  }, [])

  const setHour = useCallback((hour: number) => {
    touched.current = true

    setDraft((current) => (current ? { ...current, hour } : current))
  }, [])

  // A schedule with no days would never run, so the last one standing cannot be
  // turned off — it is switched by picking another instead.
  const toggleDay = useCallback((day: number) => {
    touched.current = true

    setDraft((current) => {
      if (!current) {
        return current
      }

      const on = current.days.includes(day)
      const days = on
        ? current.days.filter((kept) => kept !== day)
        : [...current.days, day].sort((a, b) => a - b)

      return days.length ? { ...current, days } : current
    })
  }, [])

  const save = useCallback(() => {
    if (!uuid || !draft) {
      return
    }

    setSaving(true)
    setError(null)

    NestClient.sitemapReport
      .setSchedule(uuid, draft)
      .then(() => {
        sendGtmEvent(ScheduleEvents[draft.schedule])
        setSaved(draft)
        onSaved?.()
      })
      .catch((cause) => setError(parseNestError(cause)))
      .finally(() => setSaving(false))
  }, [draft, onSaved, uuid])

  return {
    changed: Boolean(draft && saved && !sameSetting(draft, saved)),
    draft,
    error,
    loading,
    save,
    saving,
    setHour,
    setSchedule,
    toggleDay,
  }
}
